import { PerspectiveCamera } from "three";
import {
	RESTING_CAMERA_Z,
	ACTIVE_CAMERA_Z,
	MAX_CAMERA_Z,
	RESTING_ORBIT_RADIUS,
	ACTIVE_ORBIT_RADIUS,
	MAX_ORBIT_RADIUS,
	MAX_SPIN_SPEED,
	COIN_RADIUS,
	ORBIT_VIEWPORT_MARGIN,
} from "./constants.js";

const EXTREME_REFLOW_WIDTH = 20 * 16;
const EXTREME_REFLOW_HEIGHT = 26 * 16;
const NARROW_REFLOW_HEIGHT = 32 * 16;
const CONTENT_HEIGHT_PRESSURE_THRESHOLD = 0.38;
const CONTENT_WIDTH_PRESSURE_THRESHOLD = 0.62;
const RADIUS_PRESSURE_THRESHOLD = 1.08;

class Camera {
	camera = new PerspectiveCamera(75, 1 / 1, 0.1, 1000);
	sharedLayer = 0; // Layer for shared objects (lights, etc.)
	backgroundLayer = 1; // Layer for background coins
	foregroundLayer = 2; // Layer for winning coin
	aspectRatio = 1; // Current aspect ratio (width / height)
	portraitScale = 1; // Fallback scale factor for narrow viewports

	setup() {
		this.camera.position.z = RESTING_CAMERA_Z;

		// Initialize camera to render all layers
		this.camera.layers.enable(this.sharedLayer);
		this.camera.layers.enable(this.backgroundLayer);
		this.camera.layers.enable(this.foregroundLayer);
	}

	updateAspectRatio(width, height) {
		// Keep camera projection in sync with canvas size for correct raycasting/perspective
		const newAspect = width / height;
		this.camera.aspect = newAspect;
		this.camera.updateProjectionMatrix();

		// Store aspect ratio for portrait scale calculation
		this.aspectRatio = newAspect;

		// Calculate scale factor for portrait orientation
		// In portrait (height > width), scale down to prevent sides from being cut off
		// Scale based on aspect ratio: narrower = more scale down needed
		if (height > width) {
			// Portrait orientation: scale down proportionally to aspect ratio
			// When aspect is 0.5 (very narrow), scale to ~0.75
			// When aspect is 0.75 (less narrow), scale to ~0.85
			// This ensures coins don't get cut off on the sides
			this.portraitScale = Math.max(0.7, Math.min(0.9, 0.5 + newAspect * 0.5));
		} else {
			// Landscape orientation: no scaling needed
			this.portraitScale = 1;
		}

		// Ensure camera is still looking at the center (where coins orbit)
		// This is important when aspect ratio changes dramatically
		this.camera.lookAt(0, 0, 0);
	}

	updatePosition(z) {
		this.camera.position.z = z;
	}

	getOrbitFitScale(baseOrbitRadius, cameraZ, canvasRect = null) {
		if (baseOrbitRadius <= 0 || cameraZ <= 0) {
			return 1;
		}

		const fov = this.camera.fov * (Math.PI / 180);
		const halfViewportHeight = Math.tan(fov / 2) * cameraZ;
		const halfViewportWidth = halfViewportHeight * this.aspectRatio;
		const maxVisibleHeight = halfViewportHeight - ORBIT_VIEWPORT_MARGIN;

		if (maxVisibleHeight <= 0) {
			return 0;
		}

		const radiusWithCoin = baseOrbitRadius + COIN_RADIUS;
		const verticalFitScale = maxVisibleHeight / radiusWithCoin;

		// Preserve the original mobile feel: portrait layouts may bleed slightly on the sides,
		// but they should still stay vertically contained around the centered content.
		const isNarrowLayout = this.aspectRatio < 1;
		if (isNarrowLayout) {
			let relaxedHorizontalFitScale = 1;

			if (canvasRect?.width && canvasRect?.height) {
				const viewportWorldHeight = halfViewportHeight * 2;
				const worldUnitsPerPixel = viewportWorldHeight / canvasRect.height;
				const baseHorizontalBleedPx = Math.min(48, canvasRect.width * 0.14);
				const extremeNarrowBleedPx =
					canvasRect.width <= 22.5 * 16
						? Math.min(40, (22.5 * 16 - canvasRect.width) * 0.6)
						: 0;
				const horizontalBleedPx = baseHorizontalBleedPx + extremeNarrowBleedPx;
				const relaxedVisibleWidth =
					halfViewportWidth + horizontalBleedPx * worldUnitsPerPixel - ORBIT_VIEWPORT_MARGIN;

				if (relaxedVisibleWidth <= 0) {
					return 0;
				}

				relaxedHorizontalFitScale = relaxedVisibleWidth / radiusWithCoin;
			}

			return Math.min(1, Math.max(0, Math.min(verticalFitScale, relaxedHorizontalFitScale)));
		}

		const maxVisibleWidth = halfViewportWidth - ORBIT_VIEWPORT_MARGIN;
		if (maxVisibleWidth <= 0) {
			return 0;
		}

		const horizontalFitScale = maxVisibleWidth / radiusWithCoin;

		return Math.min(1, Math.max(0, Math.min(verticalFitScale, horizontalFitScale)));
	}

	isConstrainedReflowLayout(contentRect, canvasRect, requiredOrbitRadius, baseOrbitRadius) {
		if (
			!contentRect ||
			!canvasRect ||
			!canvasRect.width ||
			!canvasRect.height ||
			baseOrbitRadius <= 0
		) {
			return false;
		}

		const isExtremeViewportHeight = canvasRect.height <= EXTREME_REFLOW_HEIGHT;
		const isNarrowAndShortViewport =
			canvasRect.width <= EXTREME_REFLOW_WIDTH &&
			canvasRect.height <= NARROW_REFLOW_HEIGHT;

		if (isExtremeViewportHeight || isNarrowAndShortViewport) {
			return true;
		}

		const contentHeightRatio = contentRect.height / canvasRect.height;
		const contentWidthRatio = contentRect.width / canvasRect.width;
		const radiusPressure = requiredOrbitRadius / baseOrbitRadius;

		return (
			radiusPressure >= RADIUS_PRESSURE_THRESHOLD &&
			(contentHeightRatio >= CONTENT_HEIGHT_PRESSURE_THRESHOLD ||
				contentWidthRatio >= CONTENT_WIDTH_PRESSURE_THRESHOLD)
		);
	}

	getContentAwareOrbitRadius(baseOrbitRadius, cameraZ, contentRect, canvasRect) {
		if (
			!contentRect ||
			!canvasRect ||
			!canvasRect.height ||
			contentRect.width <= 0 ||
			contentRect.height <= 0
		) {
			return baseOrbitRadius;
		}

		const fov = this.camera.fov * (Math.PI / 180);
		const viewportWorldHeight = 2 * Math.tan(fov / 2) * cameraZ;
		const worldUnitsPerPixel = viewportWorldHeight / canvasRect.height;
		const contentInfluenceRadiusPx = Math.max(
			contentRect.height * 0.5,
			contentRect.width * 0.3
		);
		const contentGapPx = Math.max(12, Math.min(24, canvasRect.width * 0.05));
		const requiredOrbitRadius =
			(contentInfluenceRadiusPx + contentGapPx) * worldUnitsPerPixel + COIN_RADIUS;

		// Keep ordinary small/mobile layouts on the original orbit sizing.
		// Only switch into the accessibility expansion when the layout is under
		// real reflow pressure from extreme zoom or a heavily constrained viewport.
		if (
			!this.isConstrainedReflowLayout(
				contentRect,
				canvasRect,
				requiredOrbitRadius,
				baseOrbitRadius
			)
		) {
			return baseOrbitRadius;
		}

		const constrainedExpansionRatio = requiredOrbitRadius / baseOrbitRadius;
		const progressiveZoomBoost = Math.max(
			0,
			Math.min(0.1, (constrainedExpansionRatio - 1.1) * 0.5)
		);
		const extremeZoomBoost = Math.max(
			0,
			Math.min(0.1, (constrainedExpansionRatio - 1.35) * 0.5)
		);

		return Math.max(
			baseOrbitRadius,
			requiredOrbitRadius * (1 + progressiveZoomBoost + extremeZoomBoost)
		);
	}

	updateSpeedEffects(spinsPerSecond, state, contentRect = null, canvasRect = null) {
		// Determine base values based on state
		// Use active (larger) base values when not in ready or complete state
		const isRestingState = state === "ready" || state === "complete";
		const baseCameraZ = isRestingState ? RESTING_CAMERA_Z : ACTIVE_CAMERA_Z;

		// Normalize speed to 0-1 range based on max speed
		const normalizedSpeed = Math.min(Math.abs(spinsPerSecond) / MAX_SPIN_SPEED, 1);

		// Interpolate camera z position between base and max
		const cameraZ = baseCameraZ + (MAX_CAMERA_Z - baseCameraZ) * normalizedSpeed;

		// Update camera position
		this.camera.position.z = cameraZ;

		// Calculate base orbit radius
		const baseOrbitRadius = isRestingState
			? RESTING_ORBIT_RADIUS + (MAX_ORBIT_RADIUS - RESTING_ORBIT_RADIUS) * normalizedSpeed
			: ACTIVE_ORBIT_RADIUS + (MAX_ORBIT_RADIUS - ACTIVE_ORBIT_RADIUS) * normalizedSpeed;
		const desiredOrbitRadius = this.getContentAwareOrbitRadius(
			baseOrbitRadius,
			cameraZ,
			contentRect,
			canvasRect
		);

		// Combine the portrait fallback with a hard clamp based on the current frustum.
		const orbitFitScale = this.getOrbitFitScale(desiredOrbitRadius, cameraZ, canvasRect);
		const coinScale = Math.min(this.portraitScale, orbitFitScale);
		const hasExtremeReflowViewport =
			canvasRect?.height &&
			(canvasRect.height <= EXTREME_REFLOW_HEIGHT ||
				(canvasRect?.width &&
					canvasRect.width <= EXTREME_REFLOW_WIDTH &&
					canvasRect.height <= NARROW_REFLOW_HEIGHT));
		const isPortraitLayout = this.aspectRatio < 1;
		const portraitCoinScale =
			isPortraitLayout && !hasExtremeReflowViewport
				? Math.max(coinScale, 0.75)
				: coinScale;
		const portraitOrbitRadiusBoost =
			isPortraitLayout && !hasExtremeReflowViewport ? 1.02 : 1;
		const extremeNarrowRadiusBoost =
			hasExtremeReflowViewport ? 1.1 : 1;
		const scaledOrbitRadius =
			desiredOrbitRadius *
			portraitCoinScale *
			portraitOrbitRadiusBoost *
			extremeNarrowRadiusBoost;

		return {
			orbitRadius: scaledOrbitRadius,
			coinScale: portraitCoinScale,
		};
	}
}

export default Camera;
